import { Route, Routes, useLocation } from "react-router";
import { Suspense, lazy } from "react";
import { FallbackLoader } from "components/UI/molecules";
import { INTERNAL_PATHS } from "./paths";
import { PrivateGuardRouter } from "guards/private-guard-router";
import { DeviceVerificationGuardRouter } from "guards/device-verification-guard-router";
import { CheckoutProvider } from "context/checkout-context";

import HomePage from "../components/pages/home";
const BuyGiftCardsPage = lazy(() =>
  import("../components/pages/buy-gift-cards")
);
const BrandDetailsPage = lazy(() =>
  import("../components/pages/brand-details")
);
const SellGiftCardsPage = lazy(() =>
  import("../components/pages/sell-gift-cards")
);
const SellBrandGiftCardsPage = lazy(() =>
  import("../components/pages/sell-brand-gift-cards")
);
const CartPage = lazy(() => import("../components/pages/cart"));
const PrimaryCheckoutPage = lazy(() =>
  import("../components/pages/primary-checkout")
);
const SecondaryCheckoutPage = lazy(() =>
  import("../components/pages/secondary-checkout")
);
const OrderPage = lazy(() => import("../components/pages/order"));
const NotFoundPage = lazy(() => import("../components/pages/not-found"));
const LoginPage = lazy(() => import("../components/pages/login"));
const HowItWorksPage = lazy(() => import("../components/pages/how-it-works"));
const PrivacyPolicyPage = lazy(() =>
  import("../components/pages/privacy-policy")
);
const PartnershipsPage = lazy(() => import("../components/pages/partnerships"));
const ReferAFriendPage = lazy(() =>
  import("../components/pages/refer-a-friend")
);
const GiftCardBalancePage = lazy(() =>
  import("../components/pages/gift-card-balance")
);
const GuaranteePage = lazy(() => import("../components/pages/guarantee"));
const DisclosurePage = lazy(() => import("../components/pages/disclosure"));
const ShippingPolicyPage = lazy(() =>
  import("../components/pages/shipping-policy")
);
const BrandRestrictionsPage = lazy(() =>
  import("../components/pages/brand-restrictions")
);
const ShopPage = lazy(() => import("../components/pages/shop"));
const PressPage = lazy(() => import("../components/pages/press"));
const SignUpPage = lazy(() => import("../components/pages/sign-up"));
const DeviceVerificationPage = lazy(() =>
  import("../components/pages/device-verification")
);
const PlaygroundPage = lazy(() => import("../components/pages/playground"));
const LogoutPage = lazy(() => import("../components/pages/logout"));
const RecoverPasswordPage = lazy(() =>
  import("../components/pages/recover-password")
);
const ChangePasswordPage = lazy(() =>
  import("../components/pages/change-password")
);
const AppleAuthCallbackPage = lazy(() =>
  import("../components/pages/apple-auth-callback")
);

const {
  HOME,
  SECRET_HOME,
  BUY_GIFT_CARDS,
  BRAND_DETAILS,
  SELL_GIFT_CARDS,
  SELL_BRAND_GIFT_CARDS,
  CART,
  PRIMARY_CHECKOUT,
  SECONDARY_CHECKOUT,
  ORDERS,
  NOT_FOUND,
  USER_LOG_IN,
  HOW_IT_WORKS,
  PRIVACY_POLICY,
  PARTNER_PROGRAM,
  REFERRAL_PROGRAM,
  GIFT_CARD_BALANCE,
  GUARANTEE,
  FTC_DISCLOSURE,
  SHIPPING_POLICY,
  BRAND_RESTRICTIONS,
  SHOP,
  PRESS,
  USER_SIGN_UP,
  DEVICE_VERIFICATION,
  DEVICE_VERIFICATION_NEW,
  PLAYGROUND,
  USER_LOGOUT,
  RESET_PASSWORD,
  CHANGE_PASSWORD,
  APPLE_AUTH_CALLBACK
} = INTERNAL_PATHS;

const GenericRouteHandler = () => {
  const { pathname } = useLocation();

  const isBrandDetailsRoute = !!RegExp(BRAND_DETAILS).exec(pathname)?.[0];
  const isBrandSellRoute = !!RegExp(SELL_BRAND_GIFT_CARDS).exec(pathname)?.[0];

  const isNotFoundPage = !isBrandDetailsRoute && !isBrandSellRoute;

  return (
    <Suspense fallback={<FallbackLoader />}>
      {isBrandDetailsRoute && <BrandDetailsPage />}
      {isBrandSellRoute && <SellBrandGiftCardsPage />}
      {isNotFoundPage && <NotFoundPage />}
    </Suspense>
  );
};

export const AppRouter = () => (
  <Routes>
    <Route path={HOME} element={<HomePage />} />
    <Route path={SECRET_HOME} element={<HomePage />} />
    <Route
      path={BUY_GIFT_CARDS}
      element={
        <Suspense fallback={<FallbackLoader />}>
          <BuyGiftCardsPage />
        </Suspense>
      }
    />
    <Route
      path={SELL_GIFT_CARDS}
      element={
        <Suspense fallback={<FallbackLoader />}>
          <SellGiftCardsPage />
        </Suspense>
      }
    />
    <Route
      path={CART}
      element={
        <Suspense fallback={<FallbackLoader />}>
          <PrivateGuardRouter>
            <DeviceVerificationGuardRouter>
              <CheckoutProvider>
                <CartPage />
              </CheckoutProvider>
            </DeviceVerificationGuardRouter>
          </PrivateGuardRouter>
        </Suspense>
      }
    />
    <Route
      path={PRIMARY_CHECKOUT}
      element={
        <Suspense fallback={<FallbackLoader />}>
          <PrivateGuardRouter>
            <DeviceVerificationGuardRouter>
              <PrimaryCheckoutPage />
            </DeviceVerificationGuardRouter>
          </PrivateGuardRouter>
        </Suspense>
      }
    />
    <Route
      path={SECONDARY_CHECKOUT}
      element={
        <Suspense fallback={<FallbackLoader />}>
          <PrivateGuardRouter>
            <DeviceVerificationGuardRouter>
              <CheckoutProvider>
                <SecondaryCheckoutPage />
              </CheckoutProvider>
            </DeviceVerificationGuardRouter>
          </PrivateGuardRouter>
        </Suspense>
      }
    />
    <Route
      path={ORDERS}
      element={
        <Suspense fallback={<FallbackLoader />}>
          <PrivateGuardRouter>
            <DeviceVerificationGuardRouter>
              <OrderPage />
            </DeviceVerificationGuardRouter>
          </PrivateGuardRouter>
        </Suspense>
      }
    />
    <Route
      path={NOT_FOUND}
      element={
        <Suspense fallback={<FallbackLoader />}>
          <NotFoundPage />
        </Suspense>
      }
    />
    <Route
      path={USER_LOG_IN}
      element={
        <Suspense fallback={<FallbackLoader />}>
          <PrivateGuardRouter privacyType="loggedOut">
            <LoginPage />
          </PrivateGuardRouter>
        </Suspense>
      }
    />
    <Route
      path={HOW_IT_WORKS}
      element={
        <Suspense fallback={<FallbackLoader />}>
          <HowItWorksPage />
        </Suspense>
      }
    />
    <Route
      path={PRIVACY_POLICY}
      element={
        <Suspense fallback={<FallbackLoader />}>
          <PrivacyPolicyPage />
        </Suspense>
      }
    />
    <Route
      path={PARTNER_PROGRAM}
      element={
        <Suspense fallback={<FallbackLoader />}>
          <PartnershipsPage />
        </Suspense>
      }
    />
    <Route
      path={REFERRAL_PROGRAM}
      element={
        <Suspense fallback={<FallbackLoader />}>
          <ReferAFriendPage />
        </Suspense>
      }
    />
    <Route
      path={GIFT_CARD_BALANCE}
      element={
        <Suspense fallback={<FallbackLoader />}>
          <GiftCardBalancePage />
        </Suspense>
      }
    />
    <Route
      path={GUARANTEE}
      element={
        <Suspense fallback={<FallbackLoader />}>
          <GuaranteePage />
        </Suspense>
      }
    />
    <Route
      path={FTC_DISCLOSURE}
      element={
        <Suspense fallback={<FallbackLoader />}>
          <DisclosurePage />
        </Suspense>
      }
    />
    <Route
      path={SHIPPING_POLICY}
      element={
        <Suspense fallback={<FallbackLoader />}>
          <ShippingPolicyPage />
        </Suspense>
      }
    />
    <Route
      path={BRAND_RESTRICTIONS}
      element={
        <Suspense fallback={<FallbackLoader />}>
          <BrandRestrictionsPage />
        </Suspense>
      }
    />
    <Route
      path={SHOP}
      element={
        <Suspense fallback={<FallbackLoader />}>
          <ShopPage />
        </Suspense>
      }
    />
    <Route
      path={PRESS}
      element={
        <Suspense fallback={<FallbackLoader />}>
          <PressPage />
        </Suspense>
      }
    />
    <Route
      path={USER_SIGN_UP}
      element={
        <Suspense fallback={<FallbackLoader />}>
          <PrivateGuardRouter privacyType="loggedOut">
            <SignUpPage />
          </PrivateGuardRouter>
        </Suspense>
      }
    />
    <Route
      path={DEVICE_VERIFICATION}
      element={
        <Suspense fallback={<FallbackLoader />}>
          <PrivateGuardRouter>
            <DeviceVerificationPage />
          </PrivateGuardRouter>
        </Suspense>
      }
    />
    <Route
      path={DEVICE_VERIFICATION_NEW}
      element={
        <Suspense fallback={<FallbackLoader />}>
          <PrivateGuardRouter>
            <DeviceVerificationPage />
          </PrivateGuardRouter>
        </Suspense>
      }
    />
    <Route
      path={PLAYGROUND}
      element={
        <Suspense fallback={<FallbackLoader />}>
          <PrivateGuardRouter privacyType="loggedOut">
            <PlaygroundPage />
          </PrivateGuardRouter>
        </Suspense>
      }
    />
    <Route
      path={USER_LOGOUT}
      element={
        <Suspense fallback={<FallbackLoader />}>
          <PrivateGuardRouter>
            <LogoutPage />
          </PrivateGuardRouter>
        </Suspense>
      }
    />
    <Route
      path={RESET_PASSWORD}
      element={
        <Suspense fallback={<FallbackLoader />}>
          <PrivateGuardRouter privacyType="loggedOut">
            <RecoverPasswordPage />
          </PrivateGuardRouter>
        </Suspense>
      }
    />
    <Route
      path={CHANGE_PASSWORD}
      element={
        <Suspense fallback={<FallbackLoader />}>
          <PrivateGuardRouter privacyType="loggedOut">
            <ChangePasswordPage />
          </PrivateGuardRouter>
        </Suspense>
      }
    />
    <Route
      path={APPLE_AUTH_CALLBACK}
      element={
        <Suspense fallback={<FallbackLoader />}>
          <PrivateGuardRouter privacyType="loggedOut">
            <AppleAuthCallbackPage />
          </PrivateGuardRouter>
        </Suspense>
      }
    />
    <Route path="*" element={<GenericRouteHandler />} />
  </Routes>
);
